import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { DocumentService } from '@/services/document.service';
import { PdfService } from '@/services/pdf.service';
import { DocumentType, DocumentStatus, Document } from '@/models/document.model';
import { format } from 'date-fns';

export const Documents: React.FC = () => {
  const { profile } = useAuth();
  const [activeFilter, setActiveFilter] = useState<'all' | DocumentType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | DocumentStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents', activeFilter, statusFilter],
    queryFn: async () => {
      let docs = activeFilter === 'all' 
        ? await DocumentService.getAllDocuments()
        : await DocumentService.getDocumentsByType(activeFilter);
      
      if (statusFilter !== 'all') {
        docs = docs.filter(d => d.status === statusFilter);
      }
      
      if (searchTerm) {
        docs = await DocumentService.searchDocuments(searchTerm);
      }
      
      return docs;
    },
    enabled: !!profile,
  });

  const { data: expiringDocuments = [] } = useQuery({
    queryKey: ['expiringDocuments'],
    queryFn: () => DocumentService.getExpiringDocuments(30),
    enabled: !!profile,
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
      pending_review: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      contract: 'bg-blue-100 text-blue-800',
      policy: 'bg-purple-100 text-purple-800',
      employee_document: 'bg-green-100 text-green-800',
      compliance_document: 'bg-orange-100 text-orange-800',
      legal_document: 'bg-indigo-100 text-indigo-800',
      financial_document: 'bg-emerald-100 text-emerald-800',
      vendor_document: 'bg-cyan-100 text-cyan-800',
      training_material: 'bg-pink-100 text-pink-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownloadPdf = async (e: React.MouseEvent, document: Document) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await PdfService.downloadDocumentAsPdf(document);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Stats
  const totalDocuments = documents.length;
  const activeDocuments = documents.filter(d => d.status === DocumentStatus.active).length;
  const expiringCount = expiringDocuments.length;
  const totalSize = documents.reduce((sum, d) => sum + d.fileSize, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Document Management</h1>
        <p className="mt-2 text-gray-600">Centralized document storage and management</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Documents</div>
          <div className="text-2xl font-bold text-gray-900">{totalDocuments}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active Documents</div>
          <div className="text-2xl font-bold text-green-600">{activeDocuments}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Expiring Soon</div>
          <div className="text-2xl font-bold text-orange-600">{expiringCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Storage</div>
          <div className="text-2xl font-bold text-blue-600">{formatFileSize(totalSize)}</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as any)}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              {Object.values(DocumentType).map(type => (
                <option key={type} value={type}>{type.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              {Object.values(DocumentStatus).map(status => (
                <option key={status} value={status}>{status.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Link
              to="/documents/new"
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium text-center"
            >
              + Upload Document
            </Link>
          </div>
        </div>
      </div>

      {/* Expiring Documents Alert */}
      {expiringDocuments.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-orange-800">
                {expiringDocuments.length} document(s) expiring in the next 30 days
              </h3>
              <p className="text-sm text-orange-700 mt-1">
                Review and renew these documents to maintain compliance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">No documents found</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {documents.map((document) => (
              <li key={document.id}>
                <div className="block hover:bg-gray-50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <Link to={`/documents/${document.id}`} className="flex-1">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {document.name}
                        </p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(document.type)}`}>
                          {document.type.replace('_', ' ')}
                        </span>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(document.status)}`}>
                          {document.status.replace('_', ' ')}
                        </span>
                        {document.expiresAt && document.expiresAt < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Expiring Soon
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        {document.description || 'No description'}
                        {document.category && ` • ${document.category}`}
                        {document.tags.length > 0 && ` • Tags: ${document.tags.join(', ')}`}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {formatFileSize(document.fileSize)} • {document.fileName}
                        {document.expiresAt && ` • Expires: ${format(document.expiresAt, 'MMM d, yyyy')}`}
                      </p>
                    </Link>
                    <div className="flex items-center gap-3 ml-4">
                      <button
                        onClick={(e) => handleDownloadPdf(e, document)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        title="Download as PDF"
                      >
                        <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        PDF
                      </button>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {format(document.createdAt, 'MMM d, yyyy')}
                        </p>
                        <p className="text-xs text-gray-400">
                          {document.accessCount} views
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

